import { useEffect, useState } from 'react';
import { Trash2, Loader2, Star, Eye, EyeOff, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { StarRating } from '@/components/ui/StarRating';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ author: '', rating: 5, comment: '', context: '', driver_note: '', vehicle_id: '' });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/reviews');
      setItems(r.data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (item) => {
    try {
      await api.patch(`/admin/reviews/${item.id}`, { is_published: !item.is_published });
      setItems(it => it.map(x => x.id === item.id ? { ...x, is_published: !x.is_published } : x));
      toast.success(item.is_published ? 'Avis dépublié' : 'Avis publié');
    } catch { toast.error('Erreur'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cet avis ?')) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setItems(it => it.filter(x => x.id !== id));
      toast.success('Avis supprimé');
    } catch { toast.error('Erreur'); }
  };

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/reviews', form);
      toast.success('Avis ajouté');
      setOpenCreate(false);
      setForm({ author: '', rating: 5, comment: '', context: '', driver_note: '', vehicle_id: '' });
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="h2 text-anthracite-950">Avis clients</h1>
          <p className="text-muted-foreground mt-1">{items.length} avis (publiés et en attente)</p>
        </div>
        <Dialog open={openCreate} onOpenChange={setOpenCreate}>
          <DialogTrigger asChild>
            <Button data-testid="admin-add-review"><Plus className="h-4 w-4" /> Ajouter un avis</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouvel avis</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div><Label>Auteur *</Label><Input required value={form.author} onChange={e => setForm(f => ({ ...f, author: e.target.value }))} /></div>
              <div>
                <Label>Note</Label>
                <div className="flex gap-1 mt-1">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" onClick={() => setForm(f => ({ ...f, rating: n }))}>
                      <Star className={`h-6 w-6 ${n <= form.rating ? 'fill-amber-400 text-amber-400' : 'text-anthracite-200'}`} />
                    </button>
                  ))}
                </div>
              </div>
              <div><Label>Contexte</Label><Input value={form.context} onChange={e => setForm(f => ({ ...f, context: e.target.value }))} /></div>
              <div><Label>Commentaire *</Label><Textarea required value={form.comment} onChange={e => setForm(f => ({ ...f, comment: e.target.value }))} className="min-h-[100px]" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Chauffeur (privé)</Label><Input value={form.driver_note} onChange={e => setForm(f => ({ ...f, driver_note: e.target.value }))} /></div>
                <div><Label>Véhicule (privé)</Label><Input value={form.vehicle_id} onChange={e => setForm(f => ({ ...f, vehicle_id: e.target.value }))} /></div>
              </div>
              <Button type="submit" className="w-full">Ajouter l'avis</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-700" /></div>
      ) : items.length === 0 ? (
        <div className="card-base p-12 text-center text-muted-foreground">Aucun avis.</div>
      ) : (
        <div className="space-y-3">
          {items.map(r => (
            <article key={r.id} className={`card-base p-5 ${r.is_published ? '' : 'border-amber-300 bg-amber-50/40'}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-heading font-semibold text-anthracite-950">{r.author}</p>
                    <StarRating rating={r.rating} />
                    {!r.is_published && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-amber-200 text-amber-900">En attente</span>}
                  </div>
                  {r.context && <p className="text-xs text-muted-foreground mt-1">{r.context}</p>}
                  <p className="mt-2 text-sm text-anthracite-800 whitespace-pre-wrap">{r.comment}</p>
                  {(r.driver_note || r.vehicle_id) && (
                    <div className="mt-3 text-xs px-3 py-2 rounded-lg bg-anthracite-100 text-anthracite-700">
                      <span className="font-medium">Informations privées admin :</span>
                      {r.driver_note && <> Chauffeur : {r.driver_note}</>}
                      {r.vehicle_id && <> • Véhicule : {r.vehicle_id}</>}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => togglePublish(r)}>
                    {r.is_published ? <><EyeOff className="h-3.5 w-3.5" /> Dépublier</> : <><Eye className="h-3.5 w-3.5" /> Publier</>}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(r.id)} className="text-destructive border-destructive/30 hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
